import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AddToContactsButton } from '@/components/AddToContactsButton/AddToContactsButton';
import { TooltipMenuItem } from '@/components/Header/HeaderContents/HeaderSearch/HeaderMenu/TooltipMenuItem/TooltipMenuItem';
import { ReportSectionStyled } from '@/reports/shared/ReportSection/ReportSection';
import {
  Text,
  SwitchLabel,
  DataList,
  Box,
  DataListProps,
  Stack,
  SxProps,
  Icons,
  TextProps,
  Button,
  Grid,
  Link,
} from '@/theme/index';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { useScreenSize } from '@/utils/useScreenSize';

export interface SharedOverviewProps {
  reportType: string;
  Monitoring?: JSX.Element;
  /** Custom component displayed at the overview compononent footer */
  CustomOverviewFooter?: JSX.Element;
  /** If true, the **High Confidence Data Only** switch will be displayed. */
  showHighConfidenceToggle?: boolean;
  /** If true, only high confidence data will be displayed. */
  showHighConfidenceDataOnly: boolean;
  /** Callback fired when the user toggles the **High Confidence Data Only** switch. */
  setShowHighConfidenceDataOnly?: (value: boolean) => void;
}

export interface OverviewProps extends SharedOverviewProps {
  badge?: JSX.Element | null;
  title: string;
  subTitle?: JSX.Element | string;
  dataListItems?: DataListProps['items'];
  claimButton?: JSX.Element;
  overviewCaption?: string;
  children?: JSX.Element | boolean | (JSX.Element | boolean)[];
  tableStyles?: SxProps;
  Tips?: JSX.Element;
  imageCarousel?: JSX.Element;
  titleProps?: TextProps;
  alternatingColorRows?: boolean;
  containerStyling?: SxProps;
  formattedPhone?: string | null;
  email?: string | null;
  address?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  familyTreeCTA?: boolean;
  reportInFamilyTree?: boolean;
  permalink?: string;
  addRelativeToFamilyTree?: () => void;
  hasFamilyTreeCreated?: boolean;
}

/** The base overview component that creates a consistent layout for all overviews  */
export function BaseReportOverview({
  reportType,
  badge,
  title,
  subTitle,
  claimButton,
  dataListItems,
  overviewCaption,
  children, // children should be the overview items
  Monitoring,
  tableStyles,
  Tips,
  titleProps,
  imageCarousel,
  showHighConfidenceToggle = true,
  showHighConfidenceDataOnly,
  alternatingColorRows = true,
  setShowHighConfidenceDataOnly,
  containerStyling,
  CustomOverviewFooter,
  formattedPhone,
  email,
  address,
  firstName,
  lastName,
  familyTreeCTA = false,
  reportInFamilyTree = false,
  hasFamilyTreeCreated = false,
  permalink,
  addRelativeToFamilyTree = () => {},
}: OverviewProps) {
  const { isMobile } = useScreenSize();
  const { redirect } = useRedirect();
  const isOnNewDesign = useFeatureIsOn('RFRSH-2096');

  return (
    <ReportSectionStyled
      id={`${reportType}-overview`}
      data-testid="base-report-overview"
      sx={containerStyling}
    >
      {badge}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="initial"
      >
        <Stack direction="column" sx={{ width: { xs: '100%', sm: '57%' } }}>
          <Stack mb={1.5} direction="row" alignItems="center">
            <Text
              mb={0}
              variant="h1"
              {...titleProps}
              data-cy="overview-title"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {title}
            </Text>
            {reportType === 'phone' && isMobile && (
              <Stack ml={1}>
                <TooltipMenuItem hideText={true} text={title} />
              </Stack>
            )}
          </Stack>
          {Boolean(subTitle) && (
            <Text variant="h3" mt={1.5} mb={0} data-cy="overview-subtitle">
              {subTitle}
            </Text>
          )}
          {reportType === 'phone' && isMobile && (
            <Grid mb={2} sx={{ display: 'flex' }} gap={1} direction="row">
              <Grid item xs={5}>
                <Link sx={{ textDecoration: 'none' }} href={`tel:${title}`}>
                  <Button
                    sx={{
                      height: '32px',
                    }}
                    variant="contained"
                    fullWidth
                    color="info"
                  >
                    <Icons.Phone
                      sx={{ fontSize: '23px' }}
                      color={isOnNewDesign ? undefined : 'primary'}
                    />
                    <Text ml={1} sx={{ whiteSpace: 'nowrap' }}>
                      Call
                    </Text>
                  </Button>
                </Link>
              </Grid>
              <Grid item xs={7} sx={{ height: '32px' }}>
                <AddToContactsButton
                  firstName={firstName}
                  lastName={lastName}
                  number={formattedPhone}
                  address={address}
                  email={email}
                />
              </Grid>
            </Grid>
          )}
          {dataListItems && (
            <DataList
              items={dataListItems}
              tableStyles={tableStyles}
              data-cy="overview-data-list"
            />
          )}
          {overviewCaption && (
            <Text variant="h5" sx={{ mb: 0, mt: 1 }} data-cy="overview-caption">
              {overviewCaption}
            </Text>
          )}
          {Tips && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Icons.Info color="primary" sx={{ fontSize: '1.25rem' }} />
                <Text
                  variant="h5"
                  lineHeight={1.2}
                  data-cy="overview-helpful-tips"
                >
                  Helpful Tips
                </Text>
              </Stack>
              {Tips}
            </Stack>
          )}
          {isMobile && reportType === 'person' && (
            <Stack mb={1.25} sx={{ width: '145px', height: '32px' }}>
              <AddToContactsButton
                textDecoration="underlined"
                variant="text"
                firstName={firstName}
                lastName={lastName}
                number={formattedPhone}
                address={address}
                email={email}
              />
            </Stack>
          )}
          {showHighConfidenceToggle && !isMobile && (
            <SwitchLabel
              label="High Confidence Data Only"
              tooltip="High confidence data is the data our algorithms predict could be the most reliable. Since we aggregate data from public records, certain pieces of information may be corroborated by multiple sources, increasing our potential confidence in the data. You can turn this feature on or off at any time - the report will automatically update."
              checked={showHighConfidenceDataOnly}
              setChecked={setShowHighConfidenceDataOnly}
              stackStyles={{ justifyContent: 'unset', marginTop: 1 }}
              tooltipStart
              reverseOrder
            />
          )}
          <Stack gap={1}>
            {familyTreeCTA && isMobile && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (!permalink) return;
                  // If a user doesn't have a tree created, redirect with the permalink to claim the report
                  // and avoid keeping an user on a redirect loop to search again if they keep clicking this btn
                  if (!hasFamilyTreeCreated) {
                    redirect(`/family-tree?from=${permalink}`);
                    return;
                  }

                  addRelativeToFamilyTree();
                }}
                disabled={reportInFamilyTree}
              >
                {reportInFamilyTree
                  ? 'Added to Family Tree'
                  : 'Add to Family Tree'}
              </Button>
            )}
            {Monitoring && isMobile && (
              <Box mt={1} mx="auto">
                {Monitoring}
              </Box>
            )}
          </Stack>
          {claimButton && claimButton}
        </Stack>
        {imageCarousel && (
          <Stack width="40%" alignItems="center">
            {imageCarousel}
          </Stack>
        )}
        {showHighConfidenceToggle && isMobile && (
          <SwitchLabel
            label="High Confidence Data Only"
            tooltip="High confidence data is the data our algorithms predict could be the most reliable. Since we aggregate data from public records, certain pieces of information may be corroborated by multiple sources, increasing our potential confidence in the data. You can turn this feature on or off at any time - the report will automatically update."
            checked={showHighConfidenceDataOnly}
            setChecked={setShowHighConfidenceDataOnly}
            stackStyles={{ justifyContent: 'unset', marginTop: 1 }}
            tooltipStart
            reverseOrder
          />
        )}
        <Stack gap={1}>
          {familyTreeCTA && !isMobile && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                if (!permalink) return;
                // If a user doesn't have a tree created, redirect with the permalink to claim the report
                // and avoid keeping an user on a redirect loop to search again if they keep clicking this btn
                if (!hasFamilyTreeCreated) {
                  redirect(`/family-tree?from=${permalink}`);
                  return;
                }

                addRelativeToFamilyTree();
              }}
              disabled={reportInFamilyTree}
            >
              {reportInFamilyTree
                ? 'Added to Family Tree'
                : 'Add to Family Tree'}
            </Button>
          )}
          {Monitoring && !isMobile && <Box mt={0}>{Monitoring}</Box>}
        </Stack>
      </Stack>
      <Box
        sx={{
          '&>*:nth-of-type(odd)': {
            backgroundColor: alternatingColorRows ? 'grey.100' : 'inherit',
          },
        }}
      >
        {children}
      </Box>
      {Boolean(CustomOverviewFooter) && <>{CustomOverviewFooter}</>}{' '}
    </ReportSectionStyled>
  );
}
